import { NOOP_VIEW_STATUS_REPORTER } from 'tabview/activeViewsStatus/viewStatusReporter';
import React, {
  ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useReducer,
  useRef,
  memo,
  useMemo,
} from 'react';
import {
  applySelectionDecorators,
  createGraphComponent,
  focusableItems,
  selectableItems,
} from './yfilesHelper';
import { addClickHandlers, getDefaultGraphEventHandlers } from './graphEvents';
import type { GraphEventHandlers } from './graphEvents/types';
import { runLayout } from 'tabview/graphComponent/runLayout';
import { recordRenderComplete } from 'tracking/durationTracking/viewPerformance';
import { ZoomControls } from 'atomicComponents/Zoomable/ZoomControls';
import {
  GraphComponent,
  Command,
  Insets,
  LayoutData,
  LayoutDescriptor,
  PortAdjustmentPolicy,
  TimeSpan,
} from '@ardoq/yfiles';
import type { ArdoqGraphComponentViewModel } from './types';
import { FastGraphModelManager } from 'yfilesExtensions/FastGraphModelManager';
import { DataLimit } from 'components/DataLimit/DataLimit';
import { isPresentationMode } from 'env';
import { type HasViewInstanceId } from '@ardoq/graph';
import { useGraphViewPerformanceTracking } from './usePerformanceTracking';
import { ViewIds } from '@ardoq/api-types';
import { OnLayoutGraphArgs } from './onLayoutGraph';
import { noop } from 'lodash';
import { FillingDiv, GraphContainer } from './atoms';
import { ZoomContainer } from 'tabview/relationshipDiagrams/atoms';
import { HasUrlFieldValuesById } from 'tabview/graphViews/types';
import { initializeHighlightIndicator } from 'yfilesExtensions/view/highlightIndicator/initializeHighlightIndicator';
import { dispatchImageDrop } from 'yfilesExtensions/view/highlightIndicator/modernized/dispatchImageDrop';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import { HoverModeArgs } from 'yfilesExtensions/view/modernized/types';
import { Features, hasFeature } from '@ardoq/features';
import { t, type I18nLocale, DEFAULT_ARDOQ_I18NLOCALE } from '@ardoq/i18n';
import { BulkHoverPopover } from '@ardoq/popovers';
import { resolveChangedPopover } from 'scope/ChangedPopover';
import {
  UrlFieldValuesPopover,
  type UrlFieldValuesPopoverHandle,
} from './UrlFieldValuesPopover';
import { YFilesTooltipGlobalStyle } from './YFilesTooltipGlobalStyle';
import { useToolTipParentElement } from './useToolTipParentElement';

export interface ConfigureLayoutResult {
  layoutDescriptor: LayoutDescriptor;
  layoutData: LayoutData;
  duration?: TimeSpan;
  animateViewport?: boolean;
  updateContentRect?: boolean;
  targetBoundsInsets?: Insets;
  portAdjustmentPolicy?: PortAdjustmentPolicy;
}

export type ArdoqGraphComponentProperties = HasViewInstanceId &
  HasUrlFieldValuesById & {
    viewModel: ArdoqGraphComponentViewModel;
    enableStyles: boolean;
    buildGraph: (
      graphComponent: GraphComponent,
      bypassLimit: boolean
    ) => {
      isAboveLimit: boolean;
      isEmpty: boolean;
      canBypass: boolean;
      hasLayoutUpdate: boolean;
    };
    configureLayout: (graphComponent: GraphComponent) => ConfigureLayoutResult;
    configureRendering: (graphComponent: GraphComponent) => void;
    onLayoutGraph: (args: OnLayoutGraphArgs) => void;
    performanceTrackingId?: string | null;
    viewId: ViewIds;
    additionalZoomControls?: ReactNode;
    hideZoomControls?: boolean;
    contextMenu?: string;
    isViewpointMode: boolean;
    hoverMode: HoverModeArgs;
    displayShapesInsteadOfIcons?: boolean;
    graphEventHandlers?: GraphEventHandlers;
    i18nLocale?: I18nLocale;
    /**
     * The data-limit bypass is owned by the stream that builds the view model,
     * not by this component: `hasUserBypassedDataLimit` reflects it and
     * `onBypassDataLimit` dispatches the action that sets it.
     */
    hasUserBypassedDataLimit: boolean;
    onBypassDataLimit: VoidFunction;
  };
export type ArdoqGraphComponentForwardRefType = {
  graphComponent: React.RefObject<GraphComponent | null>;
  graphComponentContainer: React.RefObject<HTMLDivElement | null>;
};
const BaseArdoqGraphComponent = forwardRef<
  ArdoqGraphComponentForwardRefType,
  ArdoqGraphComponentProperties
>(
  (
    {
      hoverMode,
      configureRendering,
      buildGraph,
      configureLayout,
      onLayoutGraph,
      performanceTrackingId,
      additionalZoomControls,
      viewInstanceId,
      viewId,
      hideZoomControls,
      contextMenu,
      isViewpointMode,
      graphEventHandlers,
      hasUserBypassedDataLimit,
      onBypassDataLimit,
      i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
    },
    ref
  ) => {
    const graphComponent = useRef<GraphComponent | null>(null);
    const graphComponentContainer = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => ({
      graphComponent,
      graphComponentContainer,
    }));
    const unregisterDragAndDrop = useRef(noop);
    const previousLayoutDescriptor = useRef<LayoutDescriptor | null>(null);
    const [, forceRerender] = useReducer((count: number) => count + 1, 0);
    const urlFieldValuesPopoverRef = useRef<UrlFieldValuesPopoverHandle>(null);

    // #region temporary solution for an urgent bug. TODO: move some of this state calculation out of React/useEffect.
    /** hoverMode, expressed as a dependency array for useMemo. these are just the property values in an array. */
    const hoverModeDependencies = Array.from(Object.values(hoverMode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const hoverModeMemoized = useMemo(() => hoverMode, hoverModeDependencies);
    // #endregion

    const graphComponentEventHandlers = useMemo(
      () =>
        graphEventHandlers ??
        getDefaultGraphEventHandlers({
          viewId,
          viewInstanceId,
          isViewpointMode: () => isViewpointMode,
        }),
      [graphEventHandlers, viewId, viewInstanceId, isViewpointMode]
    );

    useEffect(
      /** initialize graphComponent */ () => {
        if (!graphComponentContainer.current || graphComponent.current) {
          return;
        }
        graphComponent.current = createGraphComponent(
          graphComponentContainer.current,
          hoverModeMemoized,
          popoverState =>
            urlFieldValuesPopoverRef.current?.setUrlFieldValuesPopover(
              popoverState
            )
        );

        unregisterDragAndDrop.current = initializeHighlightIndicator({
          graphComponent: graphComponent.current,
          viewInstanceId,
          handleImageDropCallback: dispatchImageDrop,
        });

        configureRendering(graphComponent.current);
        applySelectionDecorators(graphComponent.current);
        addClickHandlers(graphComponent.current, {
          selectableItems,
          focusableItems,
          isPresentationMode: isPresentationMode(),
          graphEventHandlers: graphComponentEventHandlers,
          isViewpointMode: () => isViewpointMode,
        });

        forceRerender(); // this was a call to forceUpdate in the original component. so the flow is a bit wrong here, it relies on a 2nd render to actually show the graph.

        return () => {
          unregisterDragAndDrop.current?.();
        };
      },
      [
        configureRendering,
        viewId,
        viewInstanceId,
        isViewpointMode,
        hoverModeMemoized,
        graphComponentEventHandlers,
      ]
    );

    useToolTipParentElement(graphComponent);

    const {
      hasLayoutUpdate: hasGraphUpdate,
      isEmpty,
      isAboveLimit,
      canBypass,
    } = graphComponent.current
      ? buildGraph(graphComponent.current, hasUserBypassedDataLimit)
      : {
          hasLayoutUpdate: false,
          isEmpty: true,
          isAboveLimit: false,
          canBypass: true,
        };
    if (graphComponent.current) {
      const {
        layoutDescriptor,
        layoutData,
        duration,
        animateViewport,
        updateContentRect,
        targetBoundsInsets,
        portAdjustmentPolicy,
      } = configureLayout(graphComponent.current);
      if (
        graphComponent.current.graphModelManager instanceof
        FastGraphModelManager
      ) {
        graphComponent.current.graphModelManager.dirty = true;
      }

      graphComponent.current.invalidate();

      runLayout({
        graphComponent: graphComponent.current,
        layoutDescriptor,
        layoutData,
        onLayoutGraph,
        performanceTrackingId,
        recordRenderComplete,
        hasGraphUpdate,
        duration,
        previousLayoutDescriptor: previousLayoutDescriptor.current,
        animateViewport,
        updateContentRect,
        targetBoundsInsets,
        portAdjustmentPolicy,
        viewInstanceId,
        viewId,
        viewStatusReporter: NOOP_VIEW_STATUS_REPORTER,
      });
      previousLayoutDescriptor.current = layoutDescriptor;
    }

    const showWarning = isAboveLimit && !hasUserBypassedDataLimit;
    // KFQ12026
    const isCanvasPrototype = hasFeature(Features.CANVAS_PROTOTYPE);

    return (
      <FillingDiv>
        {/* The tooltip yFiles renders is mounted on `document.body`, so its styling cannot live in
            a styled-component here - see `YFilesTooltipGlobalStyle`. */}
        <YFilesTooltipGlobalStyle />
        {showWarning ? (
          <DataLimit
            isAboveLimit={isAboveLimit}
            isEmpty={isEmpty}
            canBypass={canBypass}
            onBypassClick={onBypassDataLimit}
            viewId={viewId}
            isViewpointMode={isViewpointMode}
            isCanvasPrototype={isCanvasPrototype}
          />
        ) : (
          !hideZoomControls && (
            <ZoomContainer>
              <ZoomControls
                zoomIn={() =>
                  graphComponent.current?.executeCommand(
                    Command.INCREASE_ZOOM,
                    1.2
                  )
                }
                zoomOut={() =>
                  graphComponent.current?.executeCommand(
                    Command.DECREASE_ZOOM,
                    1.2
                  )
                }
                zoomCenter={() => graphComponent.current?.fitContent()}
                i18nLocale={i18nLocale}
              >
                {additionalZoomControls}
              </ZoomControls>
            </ZoomContainer>
          )
        )}

        <GraphContainer
          className="yfiles-canvascomponent"
          ref={graphComponentContainer}
          id="graphComponent"
          role="img"
          aria-label={t(
            i18nLocale,
            'tabview:blockDiagram.graph.ariaLabel',
            'Visualization of the graph'
          )}
          data-context-menu={contextMenu ?? OBJECT_CONTEXT_MENU_NAME}
        />
        <BulkHoverPopover
          rootRef={graphComponentContainer}
          resolveContent={resolveChangedPopover}
        />
        <UrlFieldValuesPopover ref={urlFieldValuesPopoverRef} viewId={viewId} />
      </FillingDiv>
    );
  }
);

export const ArdoqGraphComponent = memo(BaseArdoqGraphComponent);

export { useGraphViewPerformanceTracking };
